/**
 * "Positions & sizing" panel (R3 — observatory visibility of smoother
 * state). Binds to `index.json.refs.sizing` (`RoleSnapshot.sizing()`): one
 * row per attended slot symbol with position + marked value, a weight bar
 * against the per-symbol cap, raw-vs-smoothed signal spark, a cooldown chip,
 * and the last decision in plain words — plus one book-level gross-exposure
 * bar for the whole panel. Mirrors `MarketPanel.ts`'s poller/empty-state
 * structure.
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import type { SizingDocument, SizingSlot } from "../types/incremental";
import { renderNotPublishedYet } from "./NotPublishedYet";
import {
  buildDualSpark,
  buildSizingPanelState,
  cooldownChipText,
  coreTiltText,
  decisionText,
  isAtCap,
  markedValue,
  policyText,
  positionText,
  sizingFingerprint,
  weightBarPercent,
} from "./sizingView";
import { Poller, freshnessBadgeLabel } from "../data/polling";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import { attachTooltip } from "../ui/tooltip";
import { statLabel } from "../ui/statLabel";
import { formatMoney, formatPercent } from "../format/number";

const SPARK_WIDTH = 72;
const SPARK_HEIGHT = 24;
const SPARK_NS = "http://www.w3.org/2000/svg";

export class SizingPanel {
  private poller: Poller<SizingDocument | null> | null = null;
  private freshnessEl: HTMLSpanElement | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";

    const initial = await this.safeFetch();
    this.render(initial);

    this.poller = new Poller<SizingDocument | null>(
      async () => {
        const doc = await this.safeFetch();
        // Keyed on sizing-specific content (code review finding), not
        // `as_of_seq`/`slots.length` — the role's `index.json` advances its
        // seq on every publish, including ones where nothing sizing-related
        // changed, which previously rebuilt every row for no visible reason.
        return { key: sizingFingerprint(doc), value: doc };
      },
      (snapshot) => {
        if (snapshot.status !== "unchanged") this.render(snapshot.value);
        if (this.freshnessEl) {
          this.freshnessEl.textContent = freshnessBadgeLabel(
            snapshot.status,
            snapshot.lastSuccessAt,
          );
        }
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();
  }

  private async safeFetch(): Promise<SizingDocument | null> {
    // Not `fetchSizingSafely` here on purpose: that helper exists to stop a
    // sizing-only failure from blanking OTHER, more critical data fetched in
    // the SAME `Promise.all` (VitalsHero/FleetScreen's own fix) — this panel
    // fetches nothing else, so it keeps the same "not-published-yet vs a
    // real error" distinction every other panel's own `safeFetch` makes
    // (`MarketPanel.ts`), letting the poller's real-error/backoff path still
    // fire for a genuine failure instead of silently treating it as "no
    // data yet".
    try {
      return await this.role.sizing();
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  private render(doc: SizingDocument | null): void {
    this.container.innerHTML = "";
    const state = buildSizingPanelState(doc);

    if (state.kind === "empty") {
      this.container.appendChild(
        renderNotPublishedYet(
          "Positions & sizing",
          "Waiting for the first sizing.decision event — this panel renders a row per symbol " +
            "the moment the role's index.json names a sizing ref with at least one slot.",
        ),
      );
      return;
    }

    const root = document.createElement("div");
    root.className = "obs-sizing";

    const header = document.createElement("div");
    header.className = "obs-sizing-header";
    const asOf = document.createElement("span");
    asOf.textContent = `as of seq ${state.asOfSeq}`;
    attachTooltip(
      asOf,
      "The catalog sequence number this sizing view was folded from — find this seq in the log panel for the raw event.",
    );
    header.appendChild(asOf);
    const freshness = document.createElement("span");
    freshness.className = "obs-freshness";
    freshness.textContent = "live";
    this.freshnessEl = freshness;
    header.appendChild(freshness);
    root.appendChild(header);

    if (state.configAssumed) {
      const note = document.createElement("div");
      note.className = "obs-panel-hint";
      note.textContent =
        "Settings not yet reported — showing this app's own default caps/band/cooldown until the " +
        "role's runner reports its actual sizing.config.";
      root.appendChild(note);
    }

    root.appendChild(this.buildGrossBar(state.grossCurrentWeight, state.grossCapWeight));

    const policy = policyText(state.policy, state.buildRamp);
    if (policy) {
      const policyEl = document.createElement("div");
      policyEl.className = "obs-panel-hint obs-sizing-policy";
      policyEl.textContent = policy;
      attachTooltip(
        policyEl,
        "The risk policy the latest sized decision ran under. Core + tilt: 60% of capital in an " +
          "equal-weight core, each name tilted between 0x and 2x its base weight by the brain " +
          "(tilt 0 = core only). The core is phased in over about a day of decision time " +
          "(build-in), paced by the daily turnover budget.",
      );
      root.appendChild(policyEl);
    }

    const rows = document.createElement("div");
    rows.className = "obs-sizing-rows";
    for (const slot of state.slots) rows.appendChild(this.buildRow(slot));
    root.appendChild(rows);

    this.container.appendChild(root);
  }

  private buildGrossBar(current: number | null, cap: number): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "obs-sizing-gross";
    const label = statLabel("Book exposure", `gross, vs ${formatPercent(cap, 0)} cap`);
    wrap.appendChild(label);

    const bar = document.createElement("div");
    bar.className = "obs-sizing-gross-bar";
    const fill = document.createElement("div");
    fill.className = "obs-sizing-gross-bar-fill";
    if (isAtCap(current, cap)) fill.classList.add("obs-sizing-bar-at-cap");
    fill.style.width = `${weightBarPercent(current, cap).toFixed(1)}%`;
    bar.appendChild(fill);
    wrap.appendChild(bar);

    const value = document.createElement("span");
    value.textContent = current !== null ? formatPercent(current) : "–";
    attachTooltip(
      wrap,
      "Sum of every tracked symbol's own marked notional, divided by NAV — the book's total gross " +
        `exposure against this role's ${formatPercent(cap, 0)} whole-book cap.`,
    );
    wrap.appendChild(value);
    return wrap;
  }

  private buildRow(slot: SizingSlot): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "obs-sizing-row";

    const top = document.createElement("div");
    top.className = "obs-sizing-row-top";
    const symbol = document.createElement("span");
    symbol.className = "obs-sizing-symbol";
    symbol.textContent = slot.symbol;
    top.appendChild(symbol);

    const position = document.createElement("span");
    position.className = "obs-sizing-position";
    position.textContent = positionText(slot);
    if (slot.mark_stale) {
      // Same treatment as PortfolioPanel's own positions table (code review
      // finding: "surface mark_stale in the sizing row, like PortfolioPanel").
      position.classList.add("obs-value-down");
      attachTooltip(position, "This mark is flagged stale (mark_stale) by the snapshot itself.");
    } else {
      attachTooltip(position, "Quantity held and the latest mark price, from portfolio.snapshot.");
    }
    top.appendChild(position);

    const value = document.createElement("span");
    value.className = "obs-sizing-value obs-num-display";
    const marked = markedValue(slot);
    value.textContent = marked !== null ? formatMoney(marked) : "–";
    top.appendChild(value);
    row.appendChild(top);

    row.appendChild(this.buildWeightBar(slot));

    const allocation = coreTiltText(slot);
    if (allocation) {
      const allocationEl = document.createElement("div");
      allocationEl.className = "obs-panel-hint obs-sizing-allocation";
      allocationEl.textContent = allocation;
      attachTooltip(
        allocationEl,
        "core = this name's equal-weight base weight; tilt = the brain's tilt (multiplier " +
          "1 + tilt, clipped to 0..2x); build-in = how much of the core is phased in so far; " +
          "target = the final weight sized toward this wake (after caps, pro rata and the " +
          "turnover budget). Verbatim from the sizing.decision risk block" +
          (slot.risk_tick !== null && slot.risk_tick !== undefined
            ? ` (sized at decision tick ${slot.risk_tick}).`
            : "."),
      );
      row.appendChild(allocationEl);
    }

    const signalRow = document.createElement("div");
    signalRow.className = "obs-sizing-signal-row";
    const spark = this.buildSpark(slot);
    if (spark) {
      signalRow.appendChild(spark);
    } else {
      const noSignal = document.createElement("span");
      noSignal.className = "obs-panel-hint";
      noSignal.textContent = "no signal history yet";
      signalRow.appendChild(noSignal);
    }
    const chipText = cooldownChipText(slot);
    if (chipText) {
      const chip = document.createElement("span");
      chip.className = "obs-sizing-cooldown-chip";
      chip.textContent = chipText;
      attachTooltip(chip, "An opposite-side trade is refused while this symbol is in cooldown.");
      signalRow.appendChild(chip);
    }
    row.appendChild(signalRow);

    const decision = document.createElement("div");
    decision.className = `obs-sizing-decision obs-sizing-decision-${slot.decision}`;
    decision.textContent = decisionText(slot);
    attachTooltip(decision, `Technical: ${slot.decision}`);
    row.appendChild(decision);

    return row;
  }

  private buildWeightBar(slot: SizingSlot): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "obs-sizing-weight-bar";
    const fill = document.createElement("div");
    fill.className = "obs-sizing-weight-fill";
    if (isAtCap(slot.current_weight, slot.cap_weight)) fill.classList.add("obs-sizing-bar-at-cap");
    fill.style.width = `${weightBarPercent(slot.current_weight, slot.cap_weight).toFixed(1)}%`;
    wrap.appendChild(fill);
    if (slot.target_weight !== null) {
      const target = document.createElement("div");
      target.className = "obs-sizing-weight-target";
      target.style.left = `${weightBarPercent(slot.target_weight, slot.cap_weight).toFixed(1)}%`;
      wrap.appendChild(target);
    }
    const currentText = slot.current_weight !== null ? formatPercent(slot.current_weight) : "–";
    const targetText = slot.target_weight !== null ? formatPercent(slot.target_weight) : "–";
    attachTooltip(
      wrap,
      `Marked weight ${currentText} vs target ${targetText}, against a ${formatPercent(
        slot.cap_weight,
        0,
      )} per-symbol cap. The thin marker is the target; the fill is the current marked weight.`,
    );
    return wrap;
  }

  private buildSpark(slot: SizingSlot): SVGSVGElement | null {
    const geo = buildDualSpark(slot.raw_history, slot.smoothed_history, SPARK_WIDTH, SPARK_HEIGHT);
    if (!geo) return null;
    const svg = document.createElementNS(SPARK_NS, "svg");
    svg.setAttribute("width", String(geo.width));
    svg.setAttribute("height", String(geo.height));
    svg.setAttribute("viewBox", `0 0 ${geo.width} ${geo.height}`);
    svg.setAttribute("class", "obs-sizing-spark");

    const rawPath = document.createElementNS(SPARK_NS, "path");
    rawPath.setAttribute("d", geo.rawPath);
    rawPath.setAttribute("fill", "none");
    rawPath.setAttribute("stroke", "var(--obs-text-faint)");
    rawPath.setAttribute("stroke-width", "1");
    svg.appendChild(rawPath);

    const smoothedPath = document.createElementNS(SPARK_NS, "path");
    smoothedPath.setAttribute("d", geo.smoothedPath);
    smoothedPath.setAttribute("fill", "none");
    smoothedPath.setAttribute("stroke", "var(--obs-accent)");
    smoothedPath.setAttribute("stroke-width", "1.5");
    svg.appendChild(smoothedPath);

    attachTooltip(
      svg as unknown as HTMLElement,
      `Raw brain signal (faint) vs the EMA-smoothed signal actually sized (accent), last ${slot.raw_history.length} wakes.`,
    );
    return svg;
  }

  dispose(): void {
    this.poller?.stop();
  }
}
